import {
  Box,
  Button,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from '@chakra-ui/react';
import React from 'react';

import type { ButtonItem, Placement, ReviewTabItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { STRIP_BG, STRIP_BORDER, STRIP_H } from '../../styles/tokens';
import { Slot } from '../Slot';
import { useTabSelection } from '../TabSelectionContext';

/** Ascending by `order`, dropping any tab marked `visible:false` -- same ordering/visibility
 *  contract as `DocumentTitleNav`'s `visibleTabsInOrder`, kept local to this molecule folder per
 *  that precedent (each molecule folder stays self-contained). `.filter` on a `readonly
 *  ReviewTabItem[]` already returns a fresh mutable array, so `.sort` never mutates the config's
 *  own (readonly, `as const`) tab list. */
function visibleTabsInOrder(tabs: readonly ReviewTabItem[]): ReviewTabItem[] {
  return tabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order);
}

/** Visible actions for one `placement` group, ascending by `order` within that group. Grouping by
 *  placement FIRST and only THEN by order (`Placement` -- "'start' left, 'end'
 *  right") is what makes a mis-ordered `order` value on an 'end' action not jump ahead of a 'start'
 *  action -- see the fixture in `ReviewTransactionsTabs.test.tsx` that deliberately gives the 'end'
 *  action the smaller `order`. */
function visibleActionsFor(
  actions: readonly ButtonItem[],
  placement: Placement
): ButtonItem[] {
  return actions
    .filter((action) => action.visible && action.placement === placement)
    .sort((a, b) => a.order - b.order);
}

/**
 * RIGHT-pane tab strip -- "Review"/"Transactions" tabs plus this pane's action buttons (e.g. a
 * future "Start Review" control). `right.tabs` and `right.actions` are both `[]` in
 * `DEFAULT_CODING_SCREEN_CONFIG` so by default this renders an empty, labelled
 * `role="region"` container with NEITHER tabs NOR buttons -- "Default empty shell
 * OMITS tabs entirely (no inert/disabled tabs without panels)".
 *
 * When a config supplies tabs, each visible one renders as a Chakra `Tab` in a `TabList`, paired
 * POSITIONALLY with an (empty, shell-only) `TabPanel` in `TabPanels` -- Chakra pairs a `Tab` with
 * its `TabPanel` by mount ORDER within each list, not by id, so both lists map over the SAME
 * sorted/filtered `visibleTabs` array here, guaranteeing a Tab and its TabPanel can never drift out
 * of correspondence (mirrors `DocumentTitleNav`'s exact contract). When a config supplies actions,
 * each visible one renders as a reused Chakra `Button` (`variant` from `action.variant`), grouped by
 * `placement` ('start' left, 'end' right) then `order` within each group -- INERT, no `onClick`
 * ("no business logic"; "dumb shell"). This dual behavior is what
 * satisfies the atom-reuse requirement for BOTH the `Tabs` and `Button` atoms in one molecule.
 *
 * Each `TabPanel` is an empty shell-only placeholder (real content lives in the sibling
 * `ReviewContent`, not here) -- but Chakra 2.3.7's default Tabs base style still gives the active
 * `TabPanel` `p:4`, which would spill roughly 32px below this strip's fixed height (`STRIP_H`,
 * styles/tokens.ts -- same value `DocumentTitleNav` uses, so the left/right tab strips read as one
 * visual row height). Zeroing each panel's padding removes that content height, and clipping the
 * outer region root with `overflow:hidden` at its fixed height is the belt-and-suspenders guarantee
 * that nothing inside (tabs OR the action-button row) can ever grow the strip, without touching
 * Chakra's normal selected/non-selected panel `hidden`-attribute wiring (so the tab<->panel pairing
 * above is untouched).
 *
 * ACTION BUTTONS ARE INTERACTIVE -- mirrors `TopBarContainer`'s slot-wiring
 * contract exactly: a resolved `componentId` component is never nested INSIDE a `<Button>` (nested
 * interactive controls are invalid a11y), so each action's `Slot` FALLBACK *is* the `<Button>` itself
 * (`data-slot-key` moves onto the `Button`). `right.actions` stays wired to `Slot` here.
 *
 * CONTROLLED SELECTION: `<Tabs>` is now driven by `useTabSelection()`
 * (scoped per pane by the ancestor `TabSelectionProvider` -- `organisms/ReviewTransactionsPane`)
 * instead of Chakra's own uncontrolled internal state -- mirrors `DocumentTitleNav`'s exact
 * contract: `index` is the position of the currently-selected `contentKey` within `visibleTabs`
 * (falling back to `0` when nothing is selected yet or the selection doesn't match any
 * currently-visible tab), and `onChange` calls `select` with the newly-clicked tab's `contentKey`.
 * The clicked tab's CONTENT still doesn't render here -- `TabPanel`s stay empty shells --
 * `ReviewContent` reads the same `useTabSelection()` value to render the selected tab's slot in the
 * pane BODY, above its section list.
 */
export const ReviewTransactionsTabs: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { reviewTabs, tabs, actions } = config.right;
  const visibleTabs = visibleTabsInOrder(tabs);
  const startActions = visibleActionsFor(actions, 'start');
  const endActions = visibleActionsFor(actions, 'end');
  const hasActions = startActions.length > 0 || endActions.length > 0;
  const { selectedContentKey, select } = useTabSelection();
  const index = Math.max(
    0,
    visibleTabs.findIndex((tab) => tab.contentKey === selectedContentKey)
  );

  return (
    <Box
      role="region"
      aria-label="Review and transactions tabs"
      data-content-key={reviewTabs.contentKey}
      display="flex"
      alignItems="center"
      flexShrink={0}
      h={STRIP_H}
      minH={STRIP_H}
      bg={STRIP_BG}
      borderBottom={`1px solid ${STRIP_BORDER}`}
      style={{ overflow: 'hidden' }}
    >
      {visibleTabs.length > 0 && (
        <Tabs
          flex="1"
          index={index}
          onChange={(i) => select(visibleTabs[i].contentKey)}
        >
          <TabList>
            {visibleTabs.map((tab) => (
              <Tab key={tab.contentKey} data-slot-key={tab.contentKey}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {visibleTabs.map((tab) => (
              <TabPanel key={tab.contentKey} style={{ padding: 0 }} />
            ))}
          </TabPanels>
        </Tabs>
      )}
      {hasActions && (
        <Flex
          align="center"
          justify="space-between"
          flex={visibleTabs.length > 0 ? undefined : '1'}
          ml={visibleTabs.length > 0 ? 4 : 0}
        >
          <Flex gap={2}>
            {startActions.map((action) => (
              <Slot
                key={action.contentKey}
                item={action}
                value={readState(state, action.contentId)}
                mode={mode}
                fallback={() => (
                  <Button
                    data-slot-key={action.contentKey}
                    variant={action.variant}
                  >
                    {action.label}
                  </Button>
                )}
              />
            ))}
          </Flex>
          <Flex gap={2}>
            {endActions.map((action) => (
              <Slot
                key={action.contentKey}
                item={action}
                value={readState(state, action.contentId)}
                mode={mode}
                fallback={() => (
                  <Button
                    data-slot-key={action.contentKey}
                    variant={action.variant}
                  >
                    {action.label}
                  </Button>
                )}
              />
            ))}
          </Flex>
        </Flex>
      )}
    </Box>
  );
};
